/* Spellbound glow reveal.

   One mount per [data-spellbound-glow-reveal]. Mounts on DOMContentLoaded and
   shopify:section:load, destroys on shopify:section:unload, and activates the
   matching view on shopify:block:select. Re-including the script only
   re-runs mount(), which is idempotent.

   Inside the root:
   - [data-sc-gr-slider]           range input, writes --dim (0..1) on the root
   - [data-sc-gr-view="n"]         one layer stack per view, hidden when inactive
   - [data-sc-gr-view-button="n"]  pill buttons with aria-pressed
   - [data-sc-gr-status]           sr-only live text (status_day/mid/night)
   Root data attributes: data-dark-threshold, data-status-day/mid/night.
*/
(() => {
  if (window.SpellboundGlowReveal) {
    window.SpellboundGlowReveal.mount(document);
    return;
  }

  const ROOT = '[data-spellbound-glow-reveal]';
  const instances = new WeakMap();
  const mounted = new Set();
  const warmed = new WeakSet();

  function on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    return function remove() {
      target.removeEventListener(type, handler, options);
    };
  }

  function toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function collect(scope) {
    const found = [];
    if (!scope) return found;
    if (scope.nodeType === 1 && scope.matches(ROOT)) found.push(scope);
    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll(ROOT).forEach((node) => {
        if (found.indexOf(node) === -1) found.push(node);
      });
    }
    return found;
  }

  /* Decode each image once so swapping views and dragging the slider never
     waits on a decode. Hidden views carry loading="lazy" in the markup; the
     warm-up flips them to eager after the page has settled. */
  function warm(image) {
    if (!image || warmed.has(image)) return;
    warmed.add(image);
    if (image.loading === 'lazy') image.loading = 'eager';
    if (typeof image.decode === 'function') {
      image.decode().catch(() => {
        // The browser falls back to decoding on paint.
      });
    }
  }

  function scheduleIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(callback, { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(callback, 500);
    return () => window.clearTimeout(id);
  }

  function mountOne(root) {
    if (instances.has(root)) return;
    const slider = root.querySelector('[data-sc-gr-slider]');
    if (!slider) return;

    const views = Array.from(root.querySelectorAll('[data-sc-gr-view]'));
    const buttons = Array.from(
      root.querySelectorAll('[data-sc-gr-view-button]'),
    );
    const status = root.querySelector('[data-sc-gr-status]');
    const threshold = clamp(
      toNumber(root.getAttribute('data-dark-threshold'), 50),
      0,
      100,
    );
    const messages = {
      day: root.getAttribute('data-status-day') || '',
      mid: root.getAttribute('data-status-mid') || '',
      night: root.getAttribute('data-status-night') || '',
    };
    const removals = [];
    let cancelWarm = null;
    let current = -1;

    function indexOf(view) {
      return toNumber(view.getAttribute('data-sc-gr-view'), -1);
    }

    function indexOfButton(button) {
      return toNumber(button.getAttribute('data-sc-gr-view-button'), -1);
    }

    function statusFor(value) {
      const text =
        value < 20 ? messages.day : value < 80 ? messages.mid : messages.night;
      return text || `${value}%`;
    }

    function render() {
      const value = clamp(Math.round(toNumber(slider.value, 0)), 0, 100);
      root.style.setProperty('--dim', String(value / 100));
      root.classList.toggle('is-dark', value >= threshold);
      const text = statusFor(value);
      slider.setAttribute('aria-valuetext', text);
      if (status && status.textContent !== text) status.textContent = text;
    }

    function activate(index) {
      const target = views.find((view) => indexOf(view) === index);
      if (!target) return;
      current = index;
      views.forEach((view) => {
        const active = view === target;
        view.hidden = !active;
        view.classList.toggle('is-active', active);
      });
      buttons.forEach((button) => {
        button.setAttribute(
          'aria-pressed',
          String(indexOfButton(button) === index),
        );
      });
      target.querySelectorAll('img').forEach(warm);
    }

    function warmAll() {
      cancelWarm = null;
      root.querySelectorAll('[data-sc-gr-view] img').forEach(warm);
    }

    removals.push(on(slider, 'input', render));
    removals.push(on(slider, 'change', render));
    buttons.forEach((button) => {
      removals.push(
        on(button, 'click', () => {
          const index = indexOfButton(button);
          if (index !== current) activate(index);
        }),
      );
    });

    const pressed = buttons.find(
      (button) => button.getAttribute('aria-pressed') === 'true',
    );
    const firstVisible = views.find((view) => !view.hidden);
    const initial = pressed
      ? indexOfButton(pressed)
      : firstVisible
        ? indexOf(firstVisible)
        : views.length
          ? indexOf(views[0])
          : -1;
    if (initial >= 0) activate(initial);
    render();
    cancelWarm = scheduleIdle(warmAll);

    instances.set(root, {
      activate,
      destroy() {
        removals.forEach((remove) => remove());
        if (cancelWarm) cancelWarm();
        cancelWarm = null;
      },
    });
    mounted.add(root);
  }

  function destroyOne(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instances.delete(root);
    mounted.delete(root);
    instance.destroy();
  }

  function mount(scope) {
    collect(scope || document).forEach(mountOne);
  }

  function destroy(scope) {
    if (
      !scope ||
      scope === document ||
      scope === document.documentElement ||
      scope === document.body
    ) {
      Array.from(mounted).forEach(destroyOne);
      return;
    }
    const targets = new Set(collect(scope));
    mounted.forEach((root) => {
      if (
        root === scope ||
        (typeof scope.contains === 'function' && scope.contains(root))
      ) {
        targets.add(root);
      }
    });
    targets.forEach(destroyOne);
  }

  function onBlockSelect(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const button = target.matches('[data-sc-gr-view-button]')
      ? target
      : target.querySelector &&
        target.querySelector('[data-sc-gr-view-button]');
    if (!button) return;
    const root = button.closest(ROOT);
    if (!root) return;
    mountOne(root);
    const instance = instances.get(root);
    if (instance)
      instance.activate(
        toNumber(button.getAttribute('data-sc-gr-view-button'), -1),
      );
  }

  document.addEventListener('shopify:section:load', (event) =>
    mount(event.target),
  );
  document.addEventListener('shopify:section:unload', (event) =>
    destroy(event.target),
  );
  document.addEventListener('shopify:block:select', onBlockSelect);

  window.SpellboundGlowReveal = { mount, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), {
      once: true,
    });
  } else {
    mount(document);
  }
})();
